/**
 * US-006 — Öffentlicher Stundenplan (Kundenseite, ohne Account).
 *
 * Aufruf über `/stundenplan/{token}`. Der Kunde gibt den 4-stelligen PIN ein
 * und füllt den allgemeingültigen Tagesablauf aus. Mit „Absenden & festlegen"
 * wird der Plan verbindlich gespeichert und gesperrt — danach kann ihn nur
 * noch das Senihelp24-Team ändern.
 *
 * Layout (Bearbeiten): volle Breite, zwei Spalten — links die Tagesübersicht
 * (klickbares 30-Min-Zeitraster), rechts die kompakte Tätigkeitsliste. Das
 * Hinzufügen/Bearbeiten eines Eintrags passiert in einem Drawer. Ein Klick in
 * die Tagesübersicht legt einen neuen Eintrag an (Klick-Zeit = Start, +1 h = Ende).
 *
 * Diese Seite wird in `main.tsx` VOR dem Auth-Gate gerendert und hat daher
 * keinen Zugriff auf den CRM-Login. Sie nutzt die reinen Helfer aus dem
 * Admin-Stundenplan (Validierung, Auto-Endzeit, Budget) wieder.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { BRAND, getThemeColors } from '../../lib/theme';
import { Button, Card } from '../../components/ui';
import { useIsMobile } from '../../lib/useBreakpoint';
import {
  openPublicSchedule,
  openPublicScheduleAsAdmin,
  savePublicSchedule,
  submitPublicSchedule,
  type PbScheduleBlock,
  type PublicSchedulePayload,
} from '../../lib/api';
import {
  activityLabel,
  validateBlocks,
  endForActivitySelection,
  autoEndFor,
  nextEntryTimes,
  blockMinutes,
  plannedActivityMinutes,
  pauseMinutes,
  buildDaySlots,
  isPauseBlock,
  timeToMinutes,
  fmtClock,
  dropBlockRange,
  minuteFromFraction,
  minuteFromRows,
  measureSlotRows,
  defaultPauseBlock,
  initialScrollMin,
  isNoteRequired,
  validateEntryDraft,
  DAY_ACTIVITY_BUDGET_MIN,
  type ScheduleValidationError,
} from '../pflege/tabs/PbScheduleForm';
import { ActivityPalette, pointInElement, PALETTE_STICKY_Z, type TilePoint } from '../pflege/tabs/PbScheduleForm/ActivityPalette';
import { ScheduleEntryDrawer, entryDraftErrorMessage, type EntryDraft } from '../pflege/tabs/PbScheduleForm/ScheduleEntryDrawer';

interface Props {
  token: string;
}

type DraftBlock = PbScheduleBlock & { _key: string };

let keySeq = 0;
function nextKey(): string {
  keySeq += 1;
  return `pb${keySeq}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Minuten → "X Std YY Min". */
function fmtHm(min: number): string {
  const m = Math.max(0, Math.round(min));
  return `${Math.floor(m / 60)} Std ${String(m % 60).padStart(2, '0')} Min`;
}

/** Wartezeit vor dem einen automatischen Wiederholungsversuch beim Speichern. */
export const AUTOSAVE_RETRY_MS = 700;

/** Startzeit + 1 Stunde (auf den Tag geklemmt). */
function plusOneHour(start: string): string {
  const m = timeToMinutes(start);
  if (Number.isNaN(m)) return start;
  return fmtClock(Math.min(m + 60, 23 * 60 + 59));
}

const SCHEDULE_WEEKDAY = 1;
const DAY_END_MIN = 23 * 60 + 59;

/** Drag-Zustand in der Tagesübersicht: neuen Bereich ziehen oder Block verschieben. */
type DragState =
  | { mode: 'create'; anchor: number; cur: number }
  | { mode: 'move'; key: string; blockStart: number; blockEnd: number; anchor: number; cur: number };

/** Überschneidet [startMin,endMin) einen anderen Block? `exceptKey` wird ausgelassen. */
function rangeOverlaps(startMin: number, endMin: number, exceptKey: string | null, list: DraftBlock[]): boolean {
  return list.some(b => {
    if (exceptKey && b._key === exceptKey) return false;
    const s = timeToMinutes(b.start_time);
    const e = timeToMinutes(b.end_time);
    if (Number.isNaN(s) || Number.isNaN(e)) return false;
    return startMin < e && endMin > s;
  });
}

/** Meldung zu einem Validierungsfehler (übersetzt). */
function validationMessage(err: ScheduleValidationError, tr: (key: string, opts?: Record<string, unknown>) => string): string {
  switch (err.code) {
    case 'INCOMPLETE':       return tr('public.schedule.error.incomplete');
    case 'NO_ACTIVITY':      return tr('public.schedule.error.no_activity');
    case 'END_BEFORE_START': return tr('pflege.schedule.endBeforeStartError');
    case 'OVERLAP':          return tr('public.schedule.error.overlap');
    case 'OVERLIMIT':        return tr('public.schedule.error.overlimit');
  }
}

/** Fehlercode → Kundentext für die API-Antworten (übersetzt). */
function apiErrorMessage(err: unknown, tr: (key: string, opts?: Record<string, unknown>) => string): string {
  const e = err as { response?: { status?: number; data?: { errors?: Array<{ code?: string }>; meta?: { remaining_attempts?: number } } } };
  const status = e?.response?.status;
  const code = e?.response?.data?.errors?.[0]?.code;
  const remaining = e?.response?.data?.meta?.remaining_attempts;

  if (code === 'E_INVALID_PIN' || status === 401) {
    return remaining !== undefined && remaining > 0
      ? tr('public.schedule.error.pin_wrong_remaining', { count: remaining })
      : tr('public.schedule.error.pin_wrong');
  }
  if (code === 'E_PIN_THROTTLED' || status === 429) {
    return tr('public.schedule.error.pin_throttled');
  }
  if (code === 'E_LINK_404' || status === 404) {
    return tr('public.schedule.error.link_invalid');
  }
  if (code === 'E_SCHEDULE_OVERLAP') return tr('public.schedule.error.overlap');
  if (code === 'E_SCHEDULE_OVERLIMIT') return tr('public.schedule.error.overlimit_short');
  return tr('public.schedule.error.generic');
}

export function PublicSchedulePage({ token }: Props) {
  const { t: tr } = useTranslation();
  const { dark } = useTheme();
  const tc = getThemeColors(dark);
  const isMobile = useIsMobile();

  const [pin, setPin] = useState('');
  const [stage, setStage] = useState<'pin' | 'editing' | 'done'>('pin');
  const [blocks, setBlocks] = useState<DraftBlock[]>([]);
  const [pbName, setPbName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Drawer-Entwurf (Hinzufügen/Bearbeiten). null = Drawer geschlossen.
  const [draft, setDraft] = useState<EntryDraft | null>(null);
  const [drawerError, setDrawerError] = useState<string | null>(null);

  // Autosave-Status (jeder Eintrag wird direkt gespeichert).
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Ziehen in der Tagesübersicht: neuen Bereich aufziehen ODER Block verschieben.
  const tableRef = useRef<HTMLTableElement>(null);
  const regionRef = useRef<HTMLDivElement>(null);
  const didInitialScroll = useRef(false);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  // Kachel, die gerade über die Tagesübersicht gezogen wird (Vorschau).
  const [paletteDrag, setPaletteDrag] = useState<string | null>(null);
  const [dropMin, setDropMin] = useState<number | null>(null);
  // Über den Admin-Bypass geöffnet (eingeloggter CRM-Admin, kein PIN) →
  // Speichern/Absenden müssen ebenfalls mit Bearer statt PIN laufen.
  const [asAdmin, setAsAdmin] = useState(false);

  const applyPayload = useCallback((p: PublicSchedulePayload) => {
    setPbName(p.pb_name);
    // Leerer, noch nicht festgelegter Plan → voreingestellte Pause 13:00–15:00
    // (wie im CRM; sie wird mit dem ersten Speichern übernommen).
    const loaded = p.blocks ?? [];
    const initial = loaded.length === 0 && !p.locked ? [defaultPauseBlock()] : loaded;
    setBlocks(initial.map(b => ({ ...b, _key: nextKey() })));
    setStage(p.locked ? 'done' : 'editing');
  }, []);

  // ── Admin-Bypass: eingeloggte Admins überspringen das PIN-Gate ─────────────
  // Prüft beim Mount ob ein Admin-Token in localStorage liegt. Falls ja, wird
  // der open-Endpunkt direkt mit dem Bearer-Token aufgerufen — kein PIN nötig.
  useEffect(() => {
    const adminToken = (() => { try { return localStorage.getItem('sh24.token'); } catch { return null; } })();
    if (!adminToken) return;
    // Demo tokens are accepted by the backend only if they are real Passport tokens;
    // we attempt the admin call optimistically and fall through to the PIN gate on failure.
    let cancelled = false;
    (async () => {
      try {
        const payload = await openPublicScheduleAsAdmin(token);
        if (!cancelled) { setAsAdmin(true); applyPayload(payload); }
      } catch {
        // Not a valid admin session or token — stay on PIN stage.
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Zeitraster (00–24 Uhr) beim ersten Anzeigen auf eine sinnvolle Stelle
  // scrollen: eine Stunde vor dem ersten Eintrag, sonst 06:00.
  useEffect(() => {
    if (stage !== 'editing' || didInitialScroll.current) return;
    const region = regionRef.current;
    const table = tableRef.current;
    if (!region || !table) return;
    didInitialScroll.current = true;
    const row = table.querySelector<HTMLElement>(`tr[data-slot-min="${initialScrollMin(blocks)}"]`);
    if (!row) return;
    region.scrollTop = row.getBoundingClientRect().top - region.getBoundingClientRect().top + region.scrollTop;
  }, [stage, blocks]);

  // ── PIN absenden ───────────────────────────────────────────────────────────
  const handleOpen = useCallback(async () => {
    if (!/^\d{4}$/.test(pin)) { setError(tr('public.schedule.pin_required')); return; }
    setBusy(true);
    setError(null);
    try {
      applyPayload(await openPublicSchedule(token, pin));
    } catch (err) {
      setError(apiErrorMessage(err, tr));
    } finally {
      setBusy(false);
    }
  }, [pin, token, applyPayload, tr]);

  // ── Drawer öffnen/schließen ─────────────────────────────────────────────────
  const openNew = useCallback((presetStart?: string) => {
    let start: string, end: string;
    if (presetStart) {
      start = presetStart;
      end = plusOneHour(presetStart);
    } else {
      const n = nextEntryTimes(blocks);
      start = n.start_time; end = n.end_time;
    }
    setDraft({ _key: null, care_type: '', start_time: start, end_time: end, note: null, originalNote: null });
    setDrawerError(null);
  }, [blocks]);

  const openEdit = useCallback((b: DraftBlock) => {
    setDraft({ _key: b._key, care_type: b.care_type, start_time: b.start_time, end_time: b.end_time, note: b.note, originalNote: b.note });
    setDrawerError(null);
  }, []);

  const closeDrawer = useCallback(() => { setDraft(null); setDrawerError(null); }, []);

  const openRange = useCallback((startMin: number, endMin: number) => {
    setDraft({ _key: null, care_type: '', start_time: fmtClock(startMin), end_time: fmtClock(endMin), note: null, originalNote: null });
    setDrawerError(null);
  }, []);

  /** Neuer Eintrag aus einer Kachel: Drawer mit Tätigkeit und Zeit vorbelegt (Beschreibung fehlt noch). */
  const openActivityDraft = useCallback((careType: string, start: number, end: number) => {
    setDraft({ _key: null, care_type: careType, start_time: fmtClock(start), end_time: fmtClock(end), note: null, originalNote: null });
    setDrawerError(null);
  }, []);

  // Tagesübersicht: 30-Min-Zeitraster über den Tag.
  const daySlots = useMemo(() => buildDaySlots(blocks), [blocks]);

  // Jeden Eintrag direkt (ohne Sperren) speichern. Der Server ersetzt den Plan
  // bei jedem Aufruf komplett (löschen + neu anlegen): zwei gleichzeitige
  // Anfragen konkurrieren um dieselben Zeilen (Deadlock, 500) und eine ältere
  // Antwort könnte den neueren Stand überschreiben. Deshalb läuft immer nur
  // EINE Anfrage; was währenddessen dazukommt, wird zum letzten Stand
  // zusammengefasst und danach gesendet.
  const blocksRef = useRef<DraftBlock[]>([]);
  useEffect(() => { blocksRef.current = blocks; }, [blocks]);
  const saveRunning = useRef(false);
  const savePending = useRef<DraftBlock[] | null>(null);
  const persist = useCallback(async (next: DraftBlock[]) => {
    savePending.current = next;
    if (saveRunning.current) return;
    saveRunning.current = true;
    setSaving(true);
    try {
      while (savePending.current) {
        const cur = savePending.current;
        savePending.current = null;
        const plain: PbScheduleBlock[] = cur.map(({ _key, ...b }) => ({ ...b, weekday: SCHEDULE_WEEKDAY }));
        let failure: unknown = null;
        for (let attempt = 0; attempt < 2; attempt += 1) {
          try {
            await savePublicSchedule(token, pin, plain, { asAdmin });
            failure = null;
            break;
          } catch (err) {
            failure = err;
            const status = (err as { response?: { status?: number } })?.response?.status;
            // Ein neuerer Stand wartet schon (enthält diese Änderung mit): nicht den alten wiederholen.
            if (savePending.current) break;
            // Einmal automatisch wiederholen, aber nur bei Netz-/Serverfehlern (nicht bei 4xx).
            if (status !== undefined && status < 500) break;
            if (attempt === 0) {
              await new Promise(r => setTimeout(r, AUTOSAVE_RETRY_MS));
              if (savePending.current) break;
            }
          }
        }
        if (failure === null) {
          setSavedAt(Date.now());
          if (!savePending.current) setSaveError(null);
        } else if (!savePending.current) {
          // Nur melden, wenn kein neuerer Stand folgt (er enthält diese Änderung ohnehin mit).
          const e = failure as { response?: { status?: number; data?: { errors?: Array<{ code?: string }> } } };
          if (e?.response?.data?.errors?.[0]?.code === 'E_SCHEDULE_LOCKED') {
            setStage('done');
          } else {
            const status = e?.response?.status;
            setSaveError(`${tr('public.schedule.autosave_error')} (${status ? `HTTP ${status}` : tr('public.schedule.autosave_error_network')})`);
          }
        }
      }
    } finally {
      saveRunning.current = false;
      setSaving(false);
    }
  }, [token, pin, asAdmin, tr]);

  // ── Ziehen über die Tagesübersicht ──────────────────────────────────────────
  // Slot-Startminute aus der vertikalen Mausposition über der Tabelle.
  const slotMinFromY = useCallback((clientY: number): number | null => {
    const el = tableRef.current;
    if (!el || daySlots.length === 0) return null;
    // Zeilen messen: ein hoher Eintrag macht seine Zeilen höher als 28 px, ein
    // gleichmäßiger Bruchteil der Tabellenhöhe träfe dann den falschen Slot.
    const rows = el.querySelectorAll<HTMLElement>('tr[data-slot-min]');
    for (const row of Array.from(rows)) {
      const rr = row.getBoundingClientRect();
      if (rr.height > 0 && clientY >= rr.top && clientY < rr.bottom) return Number(row.dataset.slotMin);
    }
    const r = el.getBoundingClientRect();
    const frac = (clientY - r.top) / r.height;
    const idx = Math.max(0, Math.min(daySlots.length - 1, Math.floor(frac * daySlots.length)));
    return daySlots[idx].startMin;
  }, [daySlots]);

  const blockAtMin = useCallback((min: number): DraftBlock | null => {
    return blocks.find(b => {
      const s = timeToMinutes(b.start_time), e = timeToMinutes(b.end_time);
      return !Number.isNaN(s) && !Number.isNaN(e) && min >= s && min < e;
    }) ?? null;
  }, [blocks]);

  const onGridMouseDown = useCallback((e: React.MouseEvent) => {
    const m = slotMinFromY(e.clientY);
    if (m === null) return;
    e.preventDefault();
    setMoveError(null);
    // Der ganze Eintrag ist anklickbar: erst nach dem angeklickten Element
    // suchen, die Uhrzeit allein trifft Einträge mitten in einem Slot nicht.
    const hitKey = (e.target as HTMLElement | null)?.closest?.('[data-block-key]')?.getAttribute('data-block-key') ?? null;
    const b = (hitKey ? blocks.find(x => x._key === hitKey) : null) ?? blockAtMin(m);
    if (b) {
      setDrag({ mode: 'move', key: b._key, blockStart: timeToMinutes(b.start_time), blockEnd: timeToMinutes(b.end_time), anchor: m, cur: m });
    } else {
      setDrag({ mode: 'create', anchor: m, cur: m });
    }
  }, [slotMinFromY, blockAtMin, blocks]);

  const onGridMouseMove = useCallback((e: React.MouseEvent) => {
    if (!drag) return;
    const m = slotMinFromY(e.clientY);
    if (m !== null) setDrag(d => (d ? { ...d, cur: m } : d));
  }, [drag, slotMinFromY]);

  // ── Kachel aus der Palette in die Tagesübersicht ziehen ──────────────────────
  const dropMinFromY = useCallback((clientY: number): number | null => {
    const el = tableRef.current;
    if (!el || daySlots.length === 0) return null;
    // Echte Zeilen messen (hohe Zeilen durch Beschreibungstext), linear nur als Rückfall.
    const measured = minuteFromRows(clientY, measureSlotRows(el), 30, 15);
    if (measured !== null) return measured;
    const r = el.getBoundingClientRect();
    if (r.height <= 0) return null;
    const lo = daySlots[0].startMin;
    const hi = daySlots[daySlots.length - 1].startMin + 30;
    return minuteFromFraction((clientY - r.top) / r.height, lo, hi, 15);
  }, [daySlots]);

  // Kachel abgelegt/angetippt: erst der Drawer (Beschreibung ist Pflicht), der
  // Eintrag kommt mit „Speichern" in den Plan.
  const addActivityAt = useCallback((careType: string, startMin: number) => {
    const { start, end } = dropBlockRange(careType, startMin);
    if (rangeOverlaps(start, end, null, blocks)) {
      setMoveError(tr('pflege.schedule.dropOverlapError'));
      return;
    }
    setMoveError(null);
    setError(null);
    openActivityDraft(careType, start, end);
  }, [blocks, openActivityDraft, tr]);

  const pickActivity = useCallback((careType: string) => {
    addActivityAt(careType, timeToMinutes(nextEntryTimes(blocks).start_time));
  }, [addActivityAt, blocks]);

  const onTileDrag = useCallback((_careType: string, p: TilePoint) => {
    setDropMin(pointInElement(tableRef.current, p) ? dropMinFromY(p.y) : null);
  }, [dropMinFromY]);
  const onTileDrop = useCallback((careType: string, p: TilePoint) => {
    setDropMin(null);
    setPaletteDrag(null);
    if (!pointInElement(tableRef.current, p)) return;
    const m = dropMinFromY(p.y);
    if (m !== null) addActivityAt(careType, m);
  }, [dropMinFromY, addActivityAt]);

  // Maus loslassen (auch außerhalb des Rasters) → Aktion ausführen.
  useEffect(() => {
    if (!drag) return;
    const onUp = () => {
      if (drag.mode === 'create') {
        const lo = Math.min(drag.anchor, drag.cur), hi = Math.max(drag.anchor, drag.cur);
        if (hi === lo) openNew(fmtClock(lo));   // Klick → Start, +1 h
        else openRange(lo, hi);                 // gezogen → Von..Bis
      } else {
        const delta = drag.cur - drag.anchor;
        if (delta === 0) {
          const b = blocks.find(x => x._key === drag.key);
          if (b) openEdit(b);                   // Klick auf Block → bearbeiten
        } else {
          const dur = drag.blockEnd - drag.blockStart;
          const ns = Math.max(0, Math.min(drag.blockStart + delta, DAY_END_MIN - dur));
          const ne = ns + dur;
          if (rangeOverlaps(ns, ne, drag.key, blocks)) {
            setMoveError(tr('pflege.schedule.moveOverlapError'));
          } else {
            const next = blocks.map(x => (x._key === drag.key ? { ...x, start_time: fmtClock(ns), end_time: fmtClock(ne) } : x));
            setBlocks(next);
            void persist(next);
          }
        }
      }
      setDrag(null);
    };
    document.addEventListener('mouseup', onUp);
    return () => document.removeEventListener('mouseup', onUp);
  }, [drag, blocks, openNew, openRange, openEdit, persist, tr]);

  // Live-Vorschau der Drag-Auswahl (Bereich + Überschneidungs-Warnung).
  let prevLo: number | null = null, prevHi: number | null = null, prevBad = false, prevInclusive = true;
  if (drag) {
    if (drag.mode === 'create') {
      prevLo = Math.min(drag.anchor, drag.cur); prevHi = Math.max(drag.anchor, drag.cur); prevInclusive = true;
    } else {
      const dur = drag.blockEnd - drag.blockStart;
      const ns = Math.max(0, Math.min(drag.blockStart + (drag.cur - drag.anchor), DAY_END_MIN - dur));
      prevLo = ns; prevHi = ns + dur; prevInclusive = false;
      prevBad = rangeOverlaps(ns, ns + dur, drag.key, blocks);
    }
  } else if (paletteDrag && dropMin !== null) {
    const { start, end } = dropBlockRange(paletteDrag, dropMin);
    prevLo = start; prevHi = end; prevInclusive = false;
    prevBad = rangeOverlaps(start, end, null, blocks);
  }
  // Ein 30-Min-Slot wird hervorgehoben, wenn er den Vorschau-Bereich berührt.
  const inDrag = (min: number) => prevLo !== null && prevHi !== null && (prevInclusive
    ? min >= prevLo && min <= prevHi
    : min < prevHi && min + 30 > prevLo);

  // Drawer-Feldänderungen — bei fixer Dauer (Pause/Medikamente) Bis-Zeit nachführen.
  const draftSetActivity = useCallback((careType: string) => {
    setDraft(d => {
      if (!d) return d;
      const end = endForActivitySelection(careType || null, d.start_time);
      return { ...d, care_type: careType || null, end_time: end ?? d.end_time };
    });
    setDrawerError(null);
  }, []);
  const draftSetStart = useCallback((start: string) => {
    setDraft(d => {
      if (!d) return d;
      const auto = autoEndFor(d.care_type, start);
      return { ...d, start_time: start, end_time: auto ?? d.end_time };
    });
    setDrawerError(null);
  }, []);
  const draftPatch = useCallback((patch: Partial<EntryDraft>) => {
    setDraft(d => (d ? { ...d, ...patch } : d));
    setDrawerError(null);
  }, []);

  const draftNoteRequired = draft
    ? isNoteRequired({ careType: draft.care_type, isNew: draft._key === null, originalNote: draft.originalNote })
    : false;

  const saveDraft = useCallback(() => {
    if (!draft) return;
    const others = blocks.filter(b => b._key !== draft._key);
    const code = validateEntryDraft(draft, others, draftNoteRequired);
    if (code) { setDrawerError(entryDraftErrorMessage(code, tr)); return; }
    const block: DraftBlock = {
      _key: draft._key ?? nextKey(), weekday: SCHEDULE_WEEKDAY,
      start_time: draft.start_time, end_time: draft.end_time, care_type: draft.care_type,
      note: draft.note?.trim() ? draft.note.trim() : null,
    };
    const next = draft._key ? blocks.map(b => (b._key === draft._key ? block : b)) : [...blocks, block];
    setBlocks(next);
    setDraft(null);
    setDrawerError(null);
    setError(null);
    void persist(next);
  }, [draft, blocks, draftNoteRequired, persist, tr]);

  const deleteDraft = useCallback(() => {
    if (!draft?._key) { setDraft(null); return; }
    const key = draft._key;
    const next = blocks.filter(b => b._key !== key);
    setBlocks(next);
    setDraft(null);
    setError(null);
    void persist(next);
  }, [draft, blocks, persist]);

  // ── Absenden & festlegen ─────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    const plain: PbScheduleBlock[] = blocks.map(({ _key, ...b }) => ({ ...b, weekday: SCHEDULE_WEEKDAY }));
    const violation = validateBlocks(plain);
    if (violation) { setError(validationMessage(violation, tr)); setConfirming(false); return; }

    setBusy(true);
    setError(null);
    try {
      const res = await submitPublicSchedule(token, pin, plain, { asAdmin });
      applyPayload(res);
      setStage('done');
    } catch (err) {
      const code = (err as { response?: { data?: { errors?: Array<{ code?: string }> } } })?.response?.data?.errors?.[0]?.code;
      if (code === 'E_SCHEDULE_LOCKED') { setStage('done'); }
      else { setError(apiErrorMessage(err, tr)); }
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }, [blocks, token, pin, asAdmin, applyPayload, tr]);

  const sortedBlocks = useMemo(
    () => [...blocks].sort((a, b) => a.start_time.localeCompare(b.start_time)),
    [blocks],
  );
  // Die voreingestellte Pause allein ist noch kein Plan: Absenden erst mit einer Tätigkeit.
  const hasActivity = sortedBlocks.some(b => !isPauseBlock(b));
  const plannedMin = useMemo(() => plannedActivityMinutes(blocks), [blocks]);
  const pauseMin = useMemo(() => pauseMinutes(blocks), [blocks]);
  const reserveMin = Math.max(0, DAY_ACTIVITY_BUDGET_MIN - plannedMin);
  const overLimit = plannedMin > DAY_ACTIVITY_BUDGET_MIN;
  const pct = Math.min(100, Math.round((plannedMin / DAY_ACTIVITY_BUDGET_MIN) * 100));

  const inputStyle: React.CSSProperties = {
    fontSize: 14, padding: '10px 12px', border: `1px solid ${tc.border}`, borderRadius: 8,
    background: tc.inputBg, color: tc.text, fontFamily: 'Outfit,sans-serif', outline: 'none',
    boxSizing: 'border-box', width: '100%',
  };

  const errorBox = error && (
    <div role="alert" style={{ padding: '10px 12px', background: dark ? '#1a0505' : '#fff5f5', border: '1px solid #fda29b', borderRadius: 8, fontSize: 13.5, color: '#b42318' }}>
      {error}
    </div>
  );

  const page = (children: React.ReactNode, maxWidth: number | string = 760) => (
    // height + overflowY statt minHeight: html/body/#root stehen global auf
    // overflow:hidden — sonst wäre die öffentliche Seite nicht scrollbar.
    <div style={{ height: '100vh', overflowY: 'auto', background: tc.bg, padding: '24px 16px', fontFamily: 'Outfit,sans-serif' }}>
      <div style={{ maxWidth, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontSize: 22, fontWeight: 800, color: BRAND }}>Senihelp24</span>
          <span style={{ fontSize: 14, color: tc.muted }}>{tr('public.schedule.header_subtitle')}</span>
        </div>
        {children}
      </div>
    </div>
  );

  // ── Stufe 1: PIN ───────────────────────────────────────────────────────────
  if (stage === 'pin') {
    return page(
      <Card dark={dark} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: tc.heading }}>{tr('public.schedule.pin_stage.title')}</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: tc.muted }}>
            {tr('public.schedule.pin_stage.subtitle')}
          </p>
        </div>
        <form onSubmit={e => { e.preventDefault(); handleOpen(); }} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input
            aria-label={tr('public.schedule.pin_stage.pin_aria')}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={4}
            value={pin}
            onChange={e => { setPin(e.target.value.replace(/\D/g, '').slice(0, 4)); setError(null); }}
            placeholder="••••"
            style={{ ...inputStyle, fontSize: 28, letterSpacing: '0.5em', textAlign: 'center', maxWidth: 220 }}
          />
          {errorBox}
          <div>
            <Button dark={dark} variant="primary" disabled={busy} onClick={handleOpen}>
              {busy ? tr('public.schedule.please_wait') : tr('common.action.continue')}
            </Button>
          </div>
        </form>
      </Card>,
      460,
    );
  }

  // ── Stufe 3: festgelegt (Dank) ───────────────────────────────────────────────
  if (stage === 'done') {
    return page(
      <Card dark={dark} style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 14, textAlign: 'center' }}>
        <div style={{ fontSize: 40 }}>✓</div>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: tc.heading }}>{tr('public.schedule.done.title')}</h1>
        <p style={{ margin: 0, fontSize: 14.5, color: tc.muted, lineHeight: 1.5 }}>
          {pbName
            ? <Trans i18nKey="public.schedule.done.message_with_name" values={{ name: pbName }} components={{ b: <strong /> }} />
            : tr('public.schedule.done.message_without_name')}
        </p>
        {sortedBlocks.length > 0 && (
          <div style={{ marginTop: 6, textAlign: 'left', border: `1px solid ${tc.border}`, borderRadius: 10, overflow: 'hidden' }}>
            {sortedBlocks.map(b => (
              <div key={b._key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 14px', borderBottom: `1px solid ${tc.borderSoft}` }}>
                <span style={{ fontSize: 13.5, color: tc.heading, fontWeight: 600 }}>{activityLabel(b.care_type, tr)}</span>
                <span style={{ fontSize: 13, color: tc.muted, fontVariantNumeric: 'tabular-nums' }}>{b.start_time}–{b.end_time}</span>
              </div>
            ))}
          </div>
        )}
      </Card>,
      520,
    );
  }

  // ── Tagesübersicht (links, klickbares Zeitraster) ───────────────────────────
  const dayOverview = (
    <Card dark={dark} style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: tc.heading }}>{tr('public.schedule.overview.title')}</h2>
        <p style={{ margin: '3px 0 0', fontSize: 12, color: tc.muted }}>
          {tr('public.schedule.overview.hint')}
        </p>
      </div>
      <div
        ref={regionRef}
        data-testid="public-schedule-region"
        style={{
          border: `1px solid ${tc.border}`, borderRadius: 10, overflowX: 'hidden', overflowY: 'auto',
          maxHeight: isMobile ? 'calc(100vh - 210px)' : 'calc(100vh - 150px)', minHeight: 280,
        }}
      >
        <table
          ref={tableRef}
          data-testid="public-schedule-grid"
          onMouseDown={onGridMouseDown}
          onMouseMove={onGridMouseMove}
          style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', userSelect: drag ? 'none' : undefined, cursor: drag?.mode === 'move' ? 'grabbing' : 'pointer' }}
        >
          <tbody>
            {daySlots.map((slot, i) => {
              const b = slot.blockIndex >= 0 ? blocks[slot.blockIndex] : null;
              const prevIdx = i > 0 ? daySlots[i - 1].blockIndex : -2;
              const groupStart = !b ? true : slot.blockIndex !== prevIdx;
              let span = 1;
              if (b && groupStart) {
                while (i + span < daySlots.length && daySlots[i + span].blockIndex === slot.blockIndex) span += 1;
              }
              const highlighted = !b && inDrag(slot.startMin);
              const hlBg = prevBad
                ? (dark ? 'rgba(240,68,56,0.30)' : 'rgba(240,68,56,0.18)')
                : (dark ? 'rgba(175,0,124,0.22)' : 'rgba(175,0,124,0.12)');
              return (
                <tr key={slot.startMin} data-slot-min={slot.startMin}>
                  <td style={{ borderBottom: `1px solid ${tc.borderSoft}`, borderRight: `1px solid ${tc.borderSoft}`, padding: '0 8px', fontSize: 11.5, width: 56, height: 28, whiteSpace: 'nowrap', verticalAlign: 'top', fontVariantNumeric: 'tabular-nums', color: slot.startMin % 60 === 0 ? tc.text : tc.muted, fontWeight: slot.startMin % 60 === 0 ? 600 : 400 }}>
                    {slot.label}
                  </td>
                  {!b ? (
                    <td
                      title={tr('public.schedule.overview.add_cell_title')}
                      style={{ borderBottom: `1px solid ${tc.borderSoft}`, height: 28, background: highlighted ? hlBg : undefined }}
                    />
                  ) : groupStart ? (
                    <td
                      rowSpan={span}
                      data-block-key={b._key}
                      role="button"
                      tabIndex={0}
                      onKeyDown={ev => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); openEdit(b); } }}
                      title={tr('public.schedule.overview.move_cell_title')}
                      style={{
                        borderBottom: `1px solid ${tc.border}`, verticalAlign: 'top', padding: '4px 8px',
                        cursor: drag?.mode === 'move' && drag.key === b._key ? 'grabbing' : 'grab',
                        opacity: drag?.mode === 'move' && drag.key === b._key ? 0.55 : 1,
                        background: isPauseBlock(b) ? (dark ? 'rgba(148,163,184,0.18)' : 'rgba(100,116,139,0.10)') : (dark ? 'rgba(175,0,124,0.16)' : 'rgba(175,0,124,0.08)'),
                        borderLeft: `3px solid ${isPauseBlock(b) ? '#94a3b8' : BRAND}`,
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 700, color: tc.heading, lineHeight: 1.25 }}>{b.care_type ? activityLabel(b.care_type, tr) : '—'}</div>
                      <div style={{ fontSize: 10.5, color: tc.muted, marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>{b.start_time}–{b.end_time} · {fmtHm(blockMinutes(b))}</div>
                      {b.note && <div style={{ fontSize: 10.5, color: tc.muted, marginTop: 2, fontStyle: 'italic' }}>{b.note}</div>}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );

  // ── Tätigkeitsliste (rechts, schmal, klickbar) ──────────────────────────────
  // Die Auswahl bleibt beim Scrollen oben stehen. Am Handy als einzeilige Leiste
  // über dem Raster (die Spalte liegt dort unter dem Raster), am Desktop als
  // Kacheln am Kopf der rechten Spalte.
  const stickyPalette = (
    <div
      data-testid="palette-sticky"
      style={{
        position: 'sticky', top: 0, zIndex: PALETTE_STICKY_Z,
        background: tc.bg, paddingBottom: 6,
        maxHeight: 'calc(100vh - 16px)', overflowY: isMobile ? 'visible' : 'auto',
      }}
    >
      <ActivityPalette
        dark={dark}
        variant={isMobile ? 'strip' : 'grid'}
        onPick={pickActivity}
        onDragStateChange={ct => { setPaletteDrag(ct); if (!ct) setDropMin(null); }}
        onTileDrag={onTileDrag}
        onTileDrop={onTileDrop}
      />
    </div>
  );

  const activityList = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignSelf: 'stretch' }}>
      {!isMobile && stickyPalette}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: tc.heading }}>{tr('public.schedule.activities.title')}</h2>
        <span style={{ fontSize: 11.5, color: tc.muted, fontVariantNumeric: 'tabular-nums' }} aria-live="polite">
          {saving ? tr('public.schedule.activities.saving') : savedAt !== null ? tr('public.schedule.activities.saved') : `${sortedBlocks.length}`}
        </span>
      </div>

      {sortedBlocks.length === 0 && (
        <div style={{ fontSize: 12.5, color: tc.muted, padding: '4px 2px' }}>
          {tr('public.schedule.activities.empty')}
        </div>
      )}

      {sortedBlocks.map(b => (
        <button
          key={b._key}
          type="button"
          onClick={() => openEdit(b)}
          style={{
            textAlign: 'left', width: '100%', display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 12px', border: `1px solid ${tc.border}`, borderLeft: `3px solid ${isPauseBlock(b) ? '#94a3b8' : BRAND}`,
            borderRadius: 8, background: tc.card, cursor: 'pointer', fontFamily: 'Outfit,sans-serif',
          }}
        >
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: tc.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.care_type ? activityLabel(b.care_type, tr) : '—'}</span>
            <span style={{ display: 'block', fontSize: 11, color: tc.muted, marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>{b.start_time}–{b.end_time} · {fmtHm(blockMinutes(b))}</span>
            {b.note && <span style={{ display: 'block', fontSize: 11, color: tc.muted, marginTop: 2, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.note}</span>}
          </span>
          <span style={{ color: tc.muted, fontSize: 16, lineHeight: 1 }}>›</span>
        </button>
      ))}

      <button
        type="button"
        onClick={() => openNew()}
        style={{ fontSize: 13.5, fontWeight: 600, padding: '11px', background: 'transparent', border: `1px dashed ${tc.border}`, borderRadius: 8, color: BRAND, cursor: 'pointer', fontFamily: 'Outfit,sans-serif' }}
      >
        + {tr('public.schedule.activities.add_button')}
      </button>
    </div>
  );

  // ── Drawer (Hinzufügen / Bearbeiten) ────────────────────────────────────────
  const drawer = (
    <ScheduleEntryDrawer
      draft={draft}
      dark={dark}
      error={drawerError}
      noteRequired={draftNoteRequired}
      onActivity={draftSetActivity}
      onStart={draftSetStart}
      onPatch={draftPatch}
      onSave={saveDraft}
      onDelete={deleteDraft}
      onClose={closeDrawer}
    />
  );

  // ── Stufe 2: Bearbeiten (volle Breite, zwei Spalten, scrollbar) ─────────────
  return page(
    <>
      <Card dark={dark} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h1 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: tc.heading }}>
          {pbName ? tr('public.schedule.editing.title_with_name', { name: pbName }) : tr('public.schedule.editing.title')}
        </h1>
        <p style={{ margin: 0, fontSize: 13.5, color: tc.muted, lineHeight: 1.5 }}>
          {tr('public.schedule.editing.subtitle')}
        </p>
      </Card>

      {/* Budget-Übersicht (volle Breite) */}
      <Card dark={dark} style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { label: tr('public.schedule.budget.planned'), value: fmtHm(plannedMin), accent: overLimit ? '#b42318' : undefined },
            { label: tr('public.schedule.budget.free'), value: fmtHm(reserveMin), accent: BRAND },
            { label: tr('pflege.schedule.break'), value: fmtHm(pauseMin) },
          ].map(chip => (
            <div key={chip.label} style={{ flex: 1, minWidth: 90, background: tc.cardMuted, borderRadius: 8, padding: '8px 10px' }}>
              <div style={{ fontSize: 10.5, color: tc.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{chip.label}</div>
              <div style={{ fontSize: 14, color: chip.accent ?? tc.heading, fontWeight: 700, whiteSpace: 'nowrap' }}>{chip.value}</div>
            </div>
          ))}
        </div>
        <div style={{ height: 7, borderRadius: 6, background: tc.cardMuted, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: overLimit ? '#f04438' : BRAND, transition: 'width 0.15s' }} />
        </div>
      </Card>

      {errorBox}
      {saveError && (
        <div role="alert" style={{ padding: '10px 12px', background: dark ? '#1a1206' : '#fffaeb', border: '1px solid #fec84b', borderRadius: 8, fontSize: 13, color: '#b54708', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <span>{saveError}</span>
          <button
            type="button"
            disabled={saving}
            onClick={() => { void persist(blocksRef.current); }}
            style={{ fontSize: 12.5, fontWeight: 600, padding: '6px 12px', background: 'transparent', border: '1px solid #fec84b', borderRadius: 7, color: '#b54708', cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'Outfit,sans-serif' }}
          >
            {tr('public.schedule.autosave_retry')}
          </button>
        </div>
      )}
      {moveError && (
        <div role="alert" style={{ padding: '10px 12px', background: dark ? '#1a0505' : '#fff5f5', border: '1px solid #fda29b', borderRadius: 8, fontSize: 13, color: '#b42318' }}>
          {moveError}
        </div>
      )}

      {isMobile && stickyPalette}

      {/* Zwei Spalten: Tagesübersicht links (breit), Tätigkeiten rechts (schmal) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 320px',
        gap: isMobile ? 14 : 20,
        alignItems: 'start',
      }}>
        {dayOverview}
        {activityList}
      </div>

      {/* Absenden (zentriert, gut erkennbar) */}
      <Card dark={dark} style={{ padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        {!confirming ? (
          <button
            type="button"
            disabled={busy || !hasActivity}
            onClick={() => { setError(null); setConfirming(true); }}
            style={{
              padding: '14px 44px', fontSize: 16, fontWeight: 700, color: '#fff',
              background: (busy || !hasActivity) ? '#d0a5c4' : BRAND,
              border: 'none', borderRadius: 10, cursor: (busy || !hasActivity) ? 'not-allowed' : 'pointer',
              fontFamily: 'Outfit,sans-serif', boxShadow: '0 2px 8px rgba(175,0,124,0.30)',
            }}
          >
            {tr('public.schedule.submit.button')}
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', textAlign: 'center', maxWidth: 520 }}>
            <div style={{ fontSize: 13.5, color: tc.text }}>
              <Trans i18nKey="public.schedule.submit.confirm_text" components={{ b: <strong /> }} />
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button
                type="button"
                disabled={busy}
                onClick={handleSubmit}
                style={{ padding: '13px 36px', fontSize: 15, fontWeight: 700, color: '#fff', background: busy ? '#d0a5c4' : BRAND, border: 'none', borderRadius: 10, cursor: busy ? 'not-allowed' : 'pointer', fontFamily: 'Outfit,sans-serif', boxShadow: '0 2px 8px rgba(175,0,124,0.30)' }}
              >
                {busy ? tr('public.schedule.submit.submitting') : tr('public.schedule.submit.confirm_yes')}
              </button>
              <Button dark={dark} variant="secondary" disabled={busy} onClick={() => setConfirming(false)}>
                {tr('common.action.cancel')}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {drawer}
    </>,
    1320,
  );
}
